import { Box, Container, Flex, Icon, Slide, Text } from '@chakra-ui/react';
import { FaLock } from '@react-icons/all-files/fa/FaLock';
import { useEffect, useState } from 'react';
import { decodeToken } from 'react-jwt';

import Modal from '../../../components/Modal';
import SavePasswordForm from '../../../components/SavePasswordForm';
import Button from '../../../components/ui/buttons/Button';
import PrimaryButton from '../../../components/ui/buttons/PrimaryButton';
import { useCheckExistingAccountHasPasswordQuery } from '../../../gql/generated/graphql';
import { useLocalStorage } from '../../../hooks';
import { CookieKeys, useCookie } from '../../../hooks/useCookie';
import { LocalStorageKeys } from '../../../services/localStorage';
import { JWTToken, getEmailFromJWT } from '../../../utils/auth';

const DISMISS_PERIOD = 7 * 24 * 60 * 60 * 1000;
const SHOW_DELAY = 3000;
const ONE_TIME_LINK_SUB_PREFIX = 'email|';

const SavePasswordPrompt = () => {
  const [accessToken] = useCookie<string>(CookieKeys.ACCESS_TOKEN);
  const [dismissedAtByEmail, setDismissedAtByEmail] = useLocalStorage<Record<string, number>>(
    LocalStorageKeys.SAVE_PASSWORD_PROMPT_DISMISSED_AT,
    {},
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), SHOW_DELAY);
    return () => clearTimeout(timer);
  }, []);

  const decodedToken = decodeToken<JWTToken>(accessToken ?? '');
  const email = getEmailFromJWT(decodedToken) ?? '';
  const isOneTimeLinkSession = !!decodedToken?.sub?.startsWith(ONE_TIME_LINK_SUB_PREFIX);
  const recentlyDismissed = Date.now() - (dismissedAtByEmail[email] ?? 0) < DISMISS_PERIOD;

  const { data } = useCheckExistingAccountHasPasswordQuery({
    fetchPolicy: 'network-only',
    skip: !isOneTimeLinkSession || recentlyDismissed,
  });

  if (recentlyDismissed || data?.checkExistingAccountHasPassword !== false) {
    return null;
  }

  const handleDismiss = () => {
    setDismissedAtByEmail((prev) => ({ ...prev, [email]: Date.now() }));
  };

  return (
    <Slide direction="top" in={visible} style={{ zIndex: 1200, pointerEvents: 'none' }}>
      <Container maxW="container.md" py={4}>
        <Flex
          bg="white"
          pointerEvents="auto"
          borderRadius="12px"
          borderLeft="4px solid"
          borderLeftColor="leaseEndYellow"
          boxShadow="md"
          p={4}
          align="center"
          gap={{ base: 4, md: 8 }}
          flexWrap={{ base: 'wrap', md: 'nowrap' }}
        >
          <Flex bg="iceBlue" borderRadius="8px" p={3} flexShrink={0}>
            <Icon as={FaLock} color="leaseEndBlue" boxSize="18px" />
          </Flex>
          <Box flex={1} minW="200px">
            <Text fontWeight="bold" color="leaseEndBlue">
              Skip the email link next time
            </Text>
            <Text fontSize="sm" color="gray.700">
              You signed in with a one-time link. Save a password and you can sign straight in on
              your next visit.
            </Text>
          </Box>
          <Flex flexDir="column" gap={{ base: 4, md: 2 }} mx="auto">
            <PrimaryButton size="sm" flexShrink={0} onClick={() => setModalOpen(true)}>
              SAVE A PASSWORD
            </PrimaryButton>
            <Button variant="link" flexShrink={0} fontSize="sm" onClick={handleDismiss}>
              Maybe later
            </Button>
          </Flex>
        </Flex>
        <Modal
          title="Save a password"
          subtitle="You'll sign in with your email and this password."
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          size="lg"
        >
          <SavePasswordForm onSuccess={handleDismiss} />
        </Modal>
      </Container>
    </Slide>
  );
};

export default SavePasswordPrompt;
